import {setRef, tryCall} from '../../utils';
import {Component, ReactElement, RefObject, useEffect, useRef} from 'react';
import {findDOMNode} from 'react-dom';

type Props = {children: ReactElement, onClickedOutside: (e: MouseEvent | TouchEvent) => void, active: boolean};

export class ClickOutsideListener extends Component<Props> {
	private initTimer: number = 0;

	public override componentDidMount() {
		if (this.props.active) {
			this.initListeners();
		}
	}

	public override componentDidUpdate(prevProps: Readonly<Props>) {
		if (this.props.active !== prevProps.active) {
			if (this.props.active) {
				this.initListeners();
			} else {
				this.removeListeners();
			}
		}
	}

	public override render() {
		return this.props.children;
	}

	public override componentWillUnmount() {
		this.removeListeners();
	}

	private initListeners = () => {
		this.initTimer = window.setTimeout(() => {
			document.addEventListener('click', this.handleEvent);
			document.addEventListener('touchend', this.handleEvent);
		});
	};

	private removeListeners = () => {
		clearTimeout(this.initTimer);
		document.removeEventListener('click', this.handleEvent);
		document.removeEventListener('touchend', this.handleEvent);
	};

	private handleEvent = (event: (MouseEvent | TouchEvent)) => {
		if (!this.props.active) {
			return;
		}

		const node = findDOMNode(this);
		if (node && node instanceof Element) {
			const doc = document;
			let insideDOM;

			if (event.composedPath) {
				insideDOM = event.composedPath().indexOf(node) > -1;
			} else {
				insideDOM =
					!doc.documentElement.contains(
						// @ts-expect-error returns `false` as intended when not dispatched from a Node
						event.target,
					) ||
					node.contains(
						// @ts-expect-error returns `false` as intended when not dispatched from a Node
						event.target,
					);
			}

			if (!insideDOM) {
				this.props.onClickedOutside(event);
			}
		}
	};
}

type MouseEventCallback = (event: MouseEvent) => void;

export function useClickOutsideEffect<T extends Element>(callback: MouseEventCallback, target: RefObject<T>): void {
	const callbackRef = useRef<MouseEventCallback>();
	setRef(callback, callbackRef);
	useEffect(() => {
		const handler = (event: MouseEvent) => {
			const node = target.current;
			if (node && node instanceof Element) {
				const doc = document;
				let insideDOM;

				if (event.composedPath) {
					insideDOM = event.composedPath().indexOf(node) > -1;
				} else {
					insideDOM =
						!doc.documentElement.contains(
							// @ts-expect-error returns `false` as intended when not dispatched from a Node
							event.target,
						) ||
						node.contains(
							// @ts-expect-error returns `false` as intended when not dispatched from a Node
							event.target,
						);
				}

				if (!insideDOM) {
					tryCall(callbackRef.current, event);
				}
			}
		};

		document.addEventListener('click', handler);
		return () => document.removeEventListener('click', handler);
	}, [target]);
}
